import { useState } from 'react';
import { Check } from 'lucide-react';

function AccordionItem({ title, isOpen, onToggle, children }) {
  return (
    <div className="border border-border rounded-none bg-white">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-secondary transition-colors cursor-pointer"
      >
        <span className="text-lg font-bold uppercase">{title}</span>
        <svg
          className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-6 py-4 border-t border-border">
          {children}
        </div>
      )}
    </div>
  );
}

export function ProductSpecifications({ specifications }) {
  const [openAccordion, setOpenAccordion] = useState('');
  
  const toggleAccordion = (value) => {
    setOpenAccordion(openAccordion === value ? '' : value);
  };

  if (!specifications) return null;

  return (
    <div className="mt-16">
      <h2 className="text-3xl font-black uppercase mb-8">PRODUCT SPECIFICATIONS</h2>
      
      <div className="space-y-4">
        {/* Dimensions & Colors */}
        <AccordionItem 
          title="Dimensions & Colors"
          isOpen={openAccordion === 'dimensions'}
          onToggle={() => toggleAccordion('dimensions')}
        >
          {specifications.color_variant && (
            <div className="mb-4">
              <p className="text-sm uppercase tracking-wider font-bold mb-1">Color Variant</p>
              <p className="text-muted-foreground">{specifications.color_variant}</p>
            </div>
          )}
          {specifications.detailed_dimensions && (
            <div className="mb-4">
              <p className="text-sm uppercase tracking-wider font-bold mb-2">Detailed Dimensions</p>
              <div className="grid grid-cols-2 gap-3 mono text-sm">
                {Object.entries(specifications.detailed_dimensions).map(([key, value]) => (
                  <div key={key}>
                    <span className="text-muted-foreground capitalize">{key.replace('_', ' ')}: </span>
                    <span className="font-bold">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {specifications.colors && (
            <div>
              <p className="text-sm uppercase tracking-wider font-bold mb-2">Colors</p>
              <div className="space-y-2 text-sm">
                {Object.entries(specifications.colors).map(([key, value]) => (
                  <div key={key}>
                    <span className="text-muted-foreground capitalize">{key.replace('_', ' ')}: </span>
                    <span className="font-bold">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {specifications.occupancy && (
            <div className="mt-4">
              <p className="text-sm uppercase tracking-wider font-bold mb-1">Occupancy</p>
              <p className="text-muted-foreground">{specifications.occupancy}</p>
            </div>
          )}
          {specifications.number_of_drawers && (
            <div className="mt-4">
              <p className="text-sm uppercase tracking-wider font-bold mb-1">Number of Drawers</p>
              <p className="text-muted-foreground">{specifications.number_of_drawers}</p>
            </div>
          )}
        </AccordionItem>

        {/* Material & Construction */}
        <AccordionItem 
          title="Material & Construction"
          isOpen={openAccordion === 'material'}
          onToggle={() => toggleAccordion('material')}
        >
          {specifications.material && (
            <div className="mb-4">
              <p className="text-sm uppercase tracking-wider font-bold mb-1">Material</p>
              <p className="text-muted-foreground">{specifications.material}</p>
            </div>
          )}
          {specifications.style && (
            <div>
              <p className="text-sm uppercase tracking-wider font-bold mb-1">Style</p>
              <p className="text-muted-foreground">{specifications.style}</p>
            </div>
          )}
        </AccordionItem>

        {/* Features & Quality */}
        <AccordionItem 
          title="Features & Quality"
          isOpen={openAccordion === 'features'}
          onToggle={() => toggleAccordion('features')}
        >
          {specifications.key_features && (
            <div className="mb-4 p-4 bg-accent/10 border border-accent rounded-none">
              <p className="text-sm font-bold mb-2">KEY FEATURES</p>
              <p className="text-sm text-muted-foreground">{specifications.key_features}</p>
            </div>
          )}
          {specifications.features && (
            <ul className="space-y-2">
              {specifications.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          )}
        </AccordionItem>

        {/* Installation & Certifications */}
        <AccordionItem 
          title="Installation & Certifications"
          isOpen={openAccordion === 'installation'}
          onToggle={() => toggleAccordion('installation')}
        >
          {specifications.installation && (
            <div className="mb-4">
              <p className="text-sm uppercase tracking-wider font-bold mb-1">Installation</p>
              <p className="text-muted-foreground">{specifications.installation}</p>
            </div>
          )}
          {specifications.certifications && (
            <div>
              <p className="text-sm uppercase tracking-wider font-bold mb-2">Certifications</p>
              <div className="flex flex-wrap gap-2">
                {specifications.certifications.map((cert, index) => (
                  <span key={index} className="px-3 py-1 bg-secondary border border-border rounded-none text-xs font-bold uppercase">
                    {cert}
                  </span>
                ))}
              </div>
            </div>
          )}
        </AccordionItem>
      </div>
    </div>
  );
}
